import classnames from 'classnames2';

import React from 'react';

import { IBaseProps } from '../../utils';

import { Badge } from '../../Badge';

import styles from './Tab.Option.module.scss';

export interface ITabOptionProps extends IBaseProps {
  ariaControls?: string;
  /** Text displayed as tab title */
  label?: string;
  badge?: string;
  badgeVariant?: 'neutral' | 'warning' | 'bright' | 'strong' | 'positive' | 'negative';
  activeTabIndex?: number;
  numTabsInGroup?: number;
  /** Private: Indicates the order of the tab ib relation to it's siblings */
  index?: number;
  href?: string;
  /** Accepts values "gray" or "white" to set background color of active tabs */
  activeBackgroundColor?: 'gray' | 'white';
  /** Callback function for the tab group */
  onTabClicked?(tabIndexClicked): void;
}

export const TabOption: React.FC<ITabOptionProps> = (props) => {
  const isFirstTab = props.index === 0;
  const isActiveTab = props.activeTabIndex === props.index;
  const isLeftOfActiveTab = props.index === props.activeTabIndex - 1;
  const isRightOfActiveTab = props.index === props.activeTabIndex + 1;
  const isInactiveTab = !isActiveTab && !isLeftOfActiveTab && !isRightOfActiveTab;

  const classes = classnames(props.className, {
    [`${styles.activeTab}`]: isActiveTab,
    [`${styles.inactiveTab}`]: isInactiveTab,
    [`${styles.firstInactiveTab}`]: isFirstTab && !isActiveTab,
    [`${styles.leftOfActive}`]: isLeftOfActiveTab,
    [`${styles.rightOfActive}`]: isRightOfActiveTab,
    [`${styles.grayBackground}`]: isActiveTab && props.activeBackgroundColor === `gray`,
    [`${styles.whiteBackground}`]: isActiveTab && props.activeBackgroundColor === `white`
  });

  const HTMLElement = props.href && props.href !== '' ? 'a' : 'button';

  return (
    <div role="presentation">
      <HTMLElement
        aria-controls={props.ariaControls}
        aria-current={isActiveTab}
        aria-selected={isActiveTab}
        role={HTMLElement === 'button' ? 'tab' : 'link'}
        title={props.label}
        data-test={props.dataTest}
        className={classes}
        href={props.href}
        onClick={HTMLElement === 'button' ? () => props.onTabClicked(props.index) : null}
      >
        {props.label}
        {Boolean(props.badge) && (
          <span className={styles.badge}>
            <Badge dataTest={`badge-${props.dataTest}`} text={props.badge} variant={props.badgeVariant || 'neutral'} />
          </span>
        )}
      </HTMLElement>
    </div>
  );
};

TabOption.defaultProps = {
  activeBackgroundColor: 'gray'
};
